import {
  createFileRoute,
  type SearchMiddleware,
  stripSearchParams,
} from "@tanstack/react-router";
import { isEqual, pick } from "lodash";
import type { JSX } from "react";
import type { z } from "zod";

import { graphql } from "@repo/graphql-types/gql";
import Left from "@repo/icons/default/Left";

import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { useGraphQL } from "@/hooks/use-graphql";
import {
  getTasksPageSearchDefaults,
  type TasksPageSearchSchema,
  tasksPageSearchSchema,
} from "@/lib/form-schemas/tasks-page-search-schema";
import { toUtcTimestampRange } from "@/lib/helpers/date";
import { TasksFilterButton } from "@/routes/settings/assist/tasks/-components/tasks-filter/tasks-filter-button";
import {
  TASKS_FILTER_ACTION,
  TasksFilterDrawer,
} from "@/routes/settings/assist/tasks/-components/tasks-filter/tasks-filter-drawer";
import type { TasksFilterFormValues } from "@/routes/settings/assist/tasks/-components/tasks-filter/tasks-filter-form";
import { TasksList } from "@/routes/settings/assist/tasks/-components/tasks-list/tasks-list";
import { TasksSearchInput } from "@/routes/settings/assist/tasks/-components/tasks-search/tasks-search-input";

const TasksPageQuery = graphql(/* GraphQL */ `
  query TasksPageQuery($from: timestamptz!, $to: timestamptz!) {
    nuntius_tarefa(
      where: { criadoEm: { _gte: $from, _lt: $to } }
      order_by: { criadoEm: desc }
    ) {
      ...TasksListEntryFragment
    }
  }
`);

const pickFilters = (search: TasksPageSearchSchema): TasksFilterFormValues =>
  pick(search, ["priority", "from", "to", "sort"]);

const stripSearchDefaults: SearchMiddleware<z.input<typeof tasksPageSearchSchema>> = (
  context,
) =>
  stripSearchParams<z.input<typeof tasksPageSearchSchema>>(getTasksPageSearchDefaults())(
    context,
  );

const TasksPage = (): JSX.Element => {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const filters = pickFilters(search);
  const defaultFilters = pickFilters(getTasksPageSearchDefaults());
  const queryResult = useGraphQL(TasksPageQuery, toUtcTimestampRange(filters));

  const updateSearch = (changes: Partial<TasksPageSearchSchema>): void => {
    void navigate({ search: (previous) => ({ ...previous, ...changes }), replace: true });
  };

  const openFilter = (): void => {
    void navigate({
      search: (previous) => ({ ...previous, action: TASKS_FILTER_ACTION }),
    });
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" aria-label="Voltar" />
        <HeaderTitle title="Tarefas" />
        <TasksFilterButton
          align="end"
          hasActiveFilters={!isEqual(filters, defaultFilters)}
          onClick={openFilter}
        />
      </HeaderRoot>
      <Page>
        <div className="flex flex-col gap-3">
          <TasksSearchInput value={search.q} onSearch={(q) => updateSearch({ q })} />
          <AsyncDataWrapper {...queryResult}>
            <TasksList
              tasks={queryResult.data?.nuntius_tarefa ?? []}
              tab={search.tab}
              query={search.q}
              priorities={search.priority}
              sort={search.sort}
              onTabChange={(tab) => updateSearch({ tab })}
            />
          </AsyncDataWrapper>
        </div>
      </Page>
      <TasksFilterDrawer
        open={search.action === TASKS_FILTER_ACTION}
        values={filters}
        defaultValues={defaultFilters}
        onApply={updateSearch}
      />
    </>
  );
};

export const Route = createFileRoute("/settings/assist/tasks/")({
  validateSearch: tasksPageSearchSchema,
  search: { middlewares: [stripSearchDefaults] },
  component: TasksPage,
});
